import { Check, Clock, Flag, Loader2, Paperclip, Star } from "lucide-react";
import { ContactAvatar } from "@/features/contacts/ContactAvatar";
import { MessageBodyCacheBadge, type MessageBodyCacheStatus } from "@/features/inbox/MessageBodyCacheBadge";
import { extractSenderLabel, formatMailListDate, mailListPeerLabel } from "@/lib/formatMailDate";
import { mailColorLabelCss } from "@/lib/mail/messageLabels";
import type { MailColorLabel } from "@/lib/mail/messageLabels";
import { cn } from "@/lib/cn";
import type { MailMessageSummary } from "@/lib/types/mail";

type Props = {
  subject: string;
  from: string;
  to?: string;
  /** Envoyés / Brouillons : afficher le destinataire plutôt que soi-même. */
  preferRecipient?: boolean;
  date: string;
  seen: boolean;
  flagged?: boolean;
  favorite?: boolean;
  colorLabel?: MailColorLabel | null;
  hasAttachments?: boolean;
  selected?: boolean;
  /** Menu contextuel ouvert sur cette ligne. */
  contextMenuActive?: boolean;
  checked?: boolean;
  showCheckbox?: boolean;
  onSelect?: (event: React.MouseEvent) => void;
  onToggleCheck?: (event: React.MouseEvent) => void;
  onContextMenu?: (event: React.MouseEvent) => void;
  bodyCacheStatus?: MessageBodyCacheStatus;
  showBodyCacheBadge?: boolean;
  /** false = corps pas prêt — ligne non cliquable. */
  bodyOpenable?: boolean;
  sendStatus?: MailMessageSummary["sendStatus"];
  sendTo?: string;
  sendScheduledAtLabel?: string;
  /** Rappel prévu (vue En attente). */
  snoozeUntilLabel?: string;
  /** Suppression brouillon en cours (évite le spinner « Préparation… »). */
  draftDeleting?: boolean;
  /** Compte source (réception unifiée). */
  accountLabel?: string;
  onHover?: () => void;
};

export function InboxMessageRow({
  subject,
  from,
  to,
  preferRecipient = false,
  date,
  seen,
  flagged = false,
  favorite = false,
  colorLabel = null,
  hasAttachments,
  selected,
  contextMenuActive = false,
  checked = false,
  showCheckbox = false,
  onSelect,
  onToggleCheck,
  onContextMenu,
  bodyCacheStatus = "idle",
  showBodyCacheBadge = false,
  bodyOpenable = true,
  sendStatus,
  sendTo,
  sendScheduledAtLabel,
  snoozeUntilLabel,
  draftDeleting = false,
  accountLabel,
  onHover,
}: Props) {
  const isOutbox = sendStatus != null;
  const isPreparing = !bodyOpenable && showBodyCacheBadge && !isOutbox && !draftDeleting;
  const peer = mailListPeerLabel({
    from,
    to,
    sendTo,
    isOutbox,
    preferRecipient,
  });
  const sender = peer.label;
  const dateLabel = formatMailListDate(date);
  const multiSelect = showCheckbox && !isOutbox;
  const showUnreadBadge = !isOutbox && !seen && !checked;
  const colorCss = mailColorLabelCss(colorLabel);
  const rowHighlighted = (selected || contextMenuActive) && !isPreparing;

  const avatarSlot = (
    <span className="relative h-10 w-10 shrink-0 overflow-visible">
      {multiSelect ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCheck?.(e);
          }}
          aria-label={checked ? "Désélectionner" : "Sélectionner"}
          aria-pressed={checked}
          title={checked ? "Désélectionner" : "Sélectionner · Maj+clic plage · Ctrl+clic"}
          className={cn(
            "group/circle relative flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-150",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)] focus-visible:ring-offset-2",
            checked ? "bg-[var(--mail-accent)] text-white shadow-sm" : ""
          )}
        >
          {checked ? (
            <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
          ) : (
            <>
              <ContactAvatar
                label={extractSenderLabel(peer.avatarRaw)}
                fromRaw={peer.avatarRaw}
                size="md"
                className="transition-opacity duration-150 group-hover/circle:opacity-0"
              />
              <span
                className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-[var(--mail-accent)] text-white opacity-0 shadow-sm transition-opacity duration-150 group-hover/circle:opacity-100"
                aria-hidden
              >
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
            </>
          )}
        </button>
      ) : (
        <>
          <ContactAvatar label={extractSenderLabel(peer.avatarRaw)} fromRaw={peer.avatarRaw} size="md" />
          {isPreparing ? (
            <span
              className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-full bg-[var(--mail-bg)]/60 backdrop-blur-[1px]"
              aria-hidden
            >
              <Loader2 className="h-4 w-4 animate-spin text-[var(--mail-accent)]" />
            </span>
          ) : null}
        </>
      )}
      {showUnreadBadge ? (
        <span
          className="pointer-events-none absolute right-0 top-0 z-20 h-2.5 w-2.5 translate-x-1/3 -translate-y-1/3 rounded-full border-2 border-[var(--mail-surface)] bg-[var(--mail-accent)] shadow-sm"
          aria-hidden
        />
      ) : null}
    </span>
  );

  return (
    <div className="px-2 py-px first:pt-1 last:pb-1">
      <div
        role="presentation"
        onContextMenu={onContextMenu}
        className={cn(
          "mail-inbox-row group relative flex w-full gap-2.5 overflow-hidden rounded-[var(--mail-radius-card)] border-l-[3px] px-2.5 py-2 transition-all duration-150",
          bodyOpenable && !isPreparing && "mail-inbox-row--openable",
          !seen && !isOutbox && "mail-inbox-row--unread",
          rowHighlighted && "mail-inbox-row--active",
          checked && !isPreparing && "mail-inbox-row--active",
          isPreparing && "mail-inbox-row--preparing",
          rowHighlighted
            ? "border-l-[var(--mail-accent)] bg-[var(--mail-accent-soft)] shadow-[var(--mail-shadow-sm)] ring-1 ring-[var(--mail-accent)]/25"
            : checked && !isPreparing
              ? "border-l-[var(--mail-accent)] bg-[var(--mail-accent)]/5 ring-1 ring-[var(--mail-accent)]/20"
              : isPreparing
                ? "border-l-transparent bg-[var(--mail-surface)]"
                : colorCss
                  ? "border-l-[var(--mail-accent)] bg-[var(--mail-surface-elevated)]"
                  : seen
                    ? "border-l-transparent"
                    : "border-l-[var(--mail-accent)] bg-[var(--mail-surface-elevated)]"
        )}
        style={colorCss && !rowHighlighted && !checked ? { borderLeftColor: colorCss } : undefined}
      >
        {avatarSlot}

        <button
          type="button"
          onClick={(e) => {
            onSelect?.(e);
          }}
          onMouseEnter={onHover}
          onFocus={onHover}
          aria-disabled={!bodyOpenable}
          aria-busy={isPreparing}
          title={
            !bodyOpenable
              ? bodyCacheStatus === "warming"
                ? "Préparation du corps en cours…"
                : "Corps en cours de mise en cache…"
              : undefined
          }
          className={cn(
            "relative z-[1] min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--mail-surface)]",
            isPreparing && "pointer-events-none cursor-wait"
          )}
        >
          <span className="flex items-start justify-between gap-2">
            <span
              className={cn(
                "block min-w-0 flex-1 truncate text-sm leading-tight",
                isPreparing || seen
                  ? "font-medium text-[var(--mail-muted)]"
                  : "font-semibold text-[var(--mail-text)]"
              )}
            >
              {sender}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              {isPreparing ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--mail-muted)]">
                  <Loader2 className="h-3 w-3 animate-spin text-[var(--mail-accent)]" aria-hidden />
                  Préparation…
                </span>
              ) : null}
              {sendStatus === "scheduled" && sendScheduledAtLabel ? (
                <span
                  className="inline-flex max-w-[6.5rem] items-center gap-0.5 truncate text-[10px] font-medium tabular-nums text-[var(--mail-accent)]"
                  title={`Envoi programmé : ${sendScheduledAtLabel}`}
                >
                  <Clock className="h-3 w-3 shrink-0" aria-hidden />
                  {sendScheduledAtLabel}
                </span>
              ) : null}
              {sendStatus === "pending" || sendStatus === "sending" ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--mail-accent)]">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  Envoi…
                </span>
              ) : null}
              {sendStatus === "failed" ? (
                <span className="text-[10px] font-medium text-red-600">Échec</span>
              ) : null}
              {!isOutbox && flagged ? (
                <Flag className="h-3 w-3 shrink-0 fill-amber-500 text-amber-500" aria-label="Important" />
              ) : null}
              {!isOutbox && favorite ? (
                <Star
                  className="h-3 w-3 shrink-0 fill-[var(--mail-accent)] text-[var(--mail-accent)]"
                  aria-label="Favori"
                />
              ) : null}
              {snoozeUntilLabel ? (
                <span
                  className="max-w-[5.5rem] truncate text-[10px] font-medium tabular-nums text-[var(--mail-accent)]"
                  title={`Rappel : ${snoozeUntilLabel}`}
                >
                  {snoozeUntilLabel}
                </span>
              ) : null}
              {showBodyCacheBadge ? (
                <MessageBodyCacheBadge status={bodyCacheStatus} />
              ) : null}
              {hasAttachments ? (
                <Paperclip
                  className="h-3.5 w-3.5 shrink-0 text-[var(--mail-muted)] opacity-70 group-hover:opacity-100"
                  strokeWidth={2}
                  aria-label="Pièces jointes"
                />
              ) : null}
              <span className="text-[11px] tabular-nums leading-none text-[var(--mail-muted)]">
                {dateLabel}
              </span>
            </span>
          </span>

          {accountLabel ? (
            <span className="mt-0.5 block min-w-0 truncate text-[10px] font-medium text-[var(--mail-accent)]">
              {accountLabel}
            </span>
          ) : null}

          <span
            className={cn(
              "mt-px block min-w-0 truncate text-xs leading-snug",
              isPreparing || seen ? "text-[var(--mail-muted)]" : "font-medium text-[var(--mail-text)]"
            )}
          >
            {subject}
          </span>
        </button>
      </div>
    </div>
  );
}
